import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { 
  Keyboard, 
  Gamepad2,
  ArrowUp, 
  ArrowDown, 
  ArrowLeft, 
  ArrowRight,
  Square
} from 'lucide-react'

const KeyboardControl = ({
  isConnected,
  sendCarCommand,
  currentDirection,
  isMoving
}) => {
  const [pressedKeys, setPressedKeys] = useState(new Set())
  const [keyboardEnabled, setKeyboardEnabled] = useState(false)

  // Key mappings
  const keyMappings = {
    // WASD keys
    'KeyW': 'car_forward',
    'KeyA': 'car_left',
    'KeyS': 'car_backward',
    'KeyD': 'car_right',
    // Arrow keys
    'ArrowUp': 'car_forward',
    'ArrowLeft': 'car_left',
    'ArrowDown': 'car_backward',
    'ArrowRight': 'car_right',
    // Space bar for stop
    'Space': 'car_stop'
  }

  // Handle key press
  const handleKeyDown = (event) => {
    if (!keyboardEnabled || !isConnected) return
    
    const key = event.code
    if (keyMappings[key] && !pressedKeys.has(key)) {
      event.preventDefault()
      setPressedKeys(prev => new Set([...prev, key]))
      
      // Handle diagonal movements when multiple keys are pressed
      const newPressedKeys = new Set([...pressedKeys, key])
      const command = getCommandFromKeys(newPressedKeys)
      if (command) {
        sendCarCommand(command)
      }
    }
  }

  // Handle key release
  const handleKeyUp = (event) => {
    if (!keyboardEnabled || !isConnected) return
    
    const key = event.code
    if (keyMappings[key]) {
      event.preventDefault()
      setPressedKeys(prev => {
        const newSet = new Set(prev)
        newSet.delete(key)
        return newSet
      })
      
      // Handle remaining pressed keys or stop
      const newPressedKeys = new Set(pressedKeys)
      newPressedKeys.delete(key)
      const command = getCommandFromKeys(newPressedKeys)
      if (command) {
        sendCarCommand(command)
      } else {
        sendCarCommand('car_stop')
      }
    }
  }

  // Get command based on combination of pressed keys
  const getCommandFromKeys = (keys) => {
    const hasUp = keys.has('KeyW') || keys.has('ArrowUp')
    const hasDown = keys.has('KeyS') || keys.has('ArrowDown')
    const hasLeft = keys.has('KeyA') || keys.has('ArrowLeft')
    const hasRight = keys.has('KeyD') || keys.has('ArrowRight')

    // Diagonal movements
    if (hasUp && hasLeft) return 'car_left_forward'
    if (hasUp && hasRight) return 'car_right_forward'
    if (hasDown && hasLeft) return 'car_left_backward'
    if (hasDown && hasRight) return 'car_right_backward'
    
    // Single direction movements
    if (hasUp) return 'car_forward'
    if (hasDown) return 'car_backward'
    if (hasLeft) return 'car_left'
    if (hasRight) return 'car_right'
    
    return null
  }

  // Enable/disable keyboard controls
  const toggleKeyboard = () => {
    setKeyboardEnabled(!keyboardEnabled)
  }

  // Focus management for keyboard controls
  useEffect(() => {
    if (keyboardEnabled) {
      // Focus on the component to capture keyboard events
      const handleFocus = () => {
        // Component is focused
      }
      
      const handleBlur = () => {
        // Component lost focus, stop all movements
        if (isConnected) {
          sendCarCommand('car_stop')
        }
        setPressedKeys(new Set())
      }

      window.addEventListener('focus', handleFocus)
      window.addEventListener('blur', handleBlur)
      
      return () => {
        window.removeEventListener('focus', handleFocus)
        window.removeEventListener('blur', handleBlur)
      }
    }
  }, [keyboardEnabled, isConnected, sendCarCommand])

  // Global keyboard event listeners
  useEffect(() => {
    if (keyboardEnabled) {
      window.addEventListener('keydown', handleKeyDown)
      window.addEventListener('keyup', handleKeyUp)
      
      return () => {
        window.removeEventListener('keydown', handleKeyDown)
        window.removeEventListener('keyup', handleKeyUp)
      }
    }
  }, [keyboardEnabled, pressedKeys, isConnected, sendCarCommand])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isConnected) {
        sendCarCommand('car_stop')
      }
    }
  }, [isConnected, sendCarCommand])

  const getKeyStatus = (keyCode) => {
    return pressedKeys.has(keyCode) ? 'active' : 'idle'
  }

  const buttonVariants = {
    idle: { scale: 1, opacity: 0.7 },
    active: { 
      scale: 1.1, 
      opacity: 1,
      boxShadow: '0 0 20px rgba(59, 130, 246, 0.5)'
    }
  }

  return (
    <div className="flex flex-col items-center space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold mb-2">Keyboard Control</h3>
        <p className="text-sm text-gray-400">
          {isConnected 
            ? keyboardEnabled 
              ? 'Use WASD or Arrow keys to control the car' 
              : 'Click "Enable Keyboard" to start'
            : 'Connect to a device first'
          }
        </p>
      </div>

      {/* Keyboard Toggle */}
      <motion.button
        onClick={toggleKeyboard}
        disabled={!isConnected}
        className={`px-6 py-3 rounded-lg border-2 transition-all duration-200 flex items-center space-x-2 ${
          keyboardEnabled
            ? 'border-green-400 bg-green-500/20 text-green-400' 
            : 'border-white/20 hover:border-primary-400/50 text-gray-400 hover:text-white'
        } ${!isConnected ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <Keyboard className="w-5 h-5" />
        <span>{keyboardEnabled ? 'Keyboard Enabled' : 'Enable Keyboard'}</span>
      </motion.button>

      {/* Control Instructions */}
      {keyboardEnabled && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/5 backdrop-blur-md rounded-lg p-4 border border-white/10"
        >
          <h4 className="text-sm font-semibold text-white mb-3 text-center">Controls</h4>
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-gray-400">W / ↑</span>
                <span className="text-green-400">Forward</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">A / ←</span>
                <span className="text-blue-400">Left</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">S / ↓</span>
                <span className="text-red-400">Backward</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">D / →</span>
                <span className="text-blue-400">Right</span>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-gray-400">WA / ↑←</span>
                <span className="text-purple-400">Left-Forward</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">WD / ↑→</span>
                <span className="text-purple-400">Right-Forward</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">SA / ↓←</span>
                <span className="text-orange-400">Left-Backward</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">SD / ↓→</span>
                <span className="text-orange-400">Right-Backward</span>
              </div>
            </div>
          </div>
          <div className="mt-3 text-center">
            <div className="flex items-center justify-center space-x-2">
              <span className="text-gray-400">Space</span>
              <span className="text-yellow-400">Stop</span>
            </div>
          </div>
        </motion.div>
      )}

      {/* Visual Key Status */}
      {keyboardEnabled && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-3 gap-3"
        >
          {/* WASD Keys */}
          <div className="space-y-2">
            <div className="text-center text-xs text-gray-400 mb-2">WASD</div>
            <div className="grid grid-cols-3 gap-1">
              <div></div>
              <motion.div
                variants={buttonVariants}
                animate={getKeyStatus('KeyW')}
                className={`keyboard-key ${getKeyStatus('KeyW')}`}
              >
                W
              </motion.div>
              <div></div>
              <motion.div
                variants={buttonVariants}
                animate={getKeyStatus('KeyA')}
                className={`keyboard-key ${getKeyStatus('KeyA')}`}
              >
                A
              </motion.div>
              <motion.div
                variants={buttonVariants}
                animate={getKeyStatus('KeyS')}
                className={`keyboard-key ${getKeyStatus('KeyS')}`}
              >
                S
              </motion.div>
              <motion.div
                variants={buttonVariants}
                animate={getKeyStatus('KeyD')}
                className={`keyboard-key ${getKeyStatus('KeyD')}`}
              >
                D
              </motion.div>
            </div>
          </div>

          {/* Arrow Keys */}
          <div className="space-y-2">
            <div className="text-center text-xs text-gray-400 mb-2">Arrows</div>
            <div className="grid grid-cols-3 gap-1">
              <div></div>
              <motion.div
                variants={buttonVariants}
                animate={getKeyStatus('ArrowUp')}
                className={`keyboard-key ${getKeyStatus('ArrowUp')}`}
              >
                <ArrowUp className="w-4 h-4" />
              </motion.div>
              <div></div>
              <motion.div
                variants={buttonVariants}
                animate={getKeyStatus('ArrowLeft')}
                className={`keyboard-key ${getKeyStatus('ArrowLeft')}`}
              >
                <ArrowLeft className="w-4 h-4" />
              </motion.div>
              <motion.div
                variants={buttonVariants}
                animate={getKeyStatus('ArrowDown')}
                className={`keyboard-key ${getKeyStatus('ArrowDown')}`}
              >
                <ArrowDown className="w-4 h-4" />
              </motion.div>
              <motion.div
                variants={buttonVariants}
                animate={getKeyStatus('ArrowRight')}
                className={`keyboard-key ${getKeyStatus('ArrowRight')}`}
              >
                <ArrowRight className="w-4 h-4" />
              </motion.div>
            </div>
          </div>

          {/* Stop Key */}
          <div className="space-y-2">
            <div className="text-center text-xs text-gray-400 mb-2">Stop</div>
            <motion.div
              variants={buttonVariants}
              animate={pressedKeys.has('Space') ? 'active' : 'idle'}
              className={`keyboard-key ${pressedKeys.has('Space') ? 'active' : 'idle'} bg-yellow-600`}
            >
              <Square className="w-4 h-4" />
            </motion.div>
          </div>
        </motion.div>
      )}

      {/* Status */}
      <div className="text-center">
        <div className="text-sm text-gray-400 mb-1">Current Direction</div>
        <div className="text-lg font-semibold text-primary-400">
          {currentDirection || 'STOP'}
        </div>
        {isMoving && (
          <motion.div
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 1, repeat: Infinity }}
            className="text-xs text-success-400 mt-1"
          >
            Moving
          </motion.div>
        )}
      </div>

      {/* Warning */}
      {keyboardEnabled && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center text-xs text-yellow-400 bg-yellow-500/10 p-3 rounded-lg border border-yellow-500/20"
        >
          ⚠️ Keep this tab focused for keyboard controls to work
        </motion.div>
      )}
    </div>
  )
}

export default KeyboardControl
